/**
 * Per-manufacturer sub-region inspect view per scope §6.1.4. Atlas Day 2.5 P5.
 *
 * Sparse substrate (Phase 0 #4: 1 active manufacturer / 1 pairing).
 * Surface verbiage reflects sparse state; classification + propagation map
 * shapes anticipate manufacturer-network expansion at future buying-scope
 * growth.
 */

import type { JSX } from 'react';
import { FIELD_CLASSIFICATIONS } from '../../../lib/atlas/classificationMaps';
import { PER_MANUFACTURER_PROPAGATION } from '../../../lib/atlas/propagationMaps';
import InspectShell from './InspectShell';
import LayerSection from './LayerSection';
import ComposabilityReservedLayer from './ComposabilityReservedLayer';

export default function PerManufacturerInspect(): JSX.Element {
  const mfFields = FIELD_CLASSIFICATIONS.manufacturers;
  const mdFields = FIELD_CLASSIFICATIONS.manufacturer_distributors;
  const mfEntries = Object.entries(mfFields);
  const mdEntries = Object.entries(mdFields);
  const allEntries = [
    ...mfEntries,
    ...mdEntries.map(([f, c]) => [`manufacturer_distributors.${f}`, c] as const),
  ];

  return (
    <InspectShell
      clusterName="Per-entity configuration"
      subRegionName="Per-manufacturer"
      surfaceLine="ISG carries products from N manufacturers; K active. Manufacturer-distributor pairings gate path eligibility → Path eligibility rule above."
    >
      <LayerSection layer={4} title="Math vs judgment">
        <p>
          Per scope §6.1.4: <code>is_active</code> = <strong>judgment</strong> (operating
          choice); <code>manufacturer_distributors</code> pairings = <strong>judgment</strong>{' '}
          (which distributors carry which manufacturers — ISG&apos;s manufacturer-network
          choices); <code>discount_pct</code> per pairing = <strong>judgment</strong>{' '}
          (per-pairing discount stance).
        </p>
      </LayerSection>

      <LayerSection layer={5} title="Mechanical vs strategic">
        <p>
          Per scope §6.1.4: all manufacturer + pairing fields are{' '}
          <strong>strategic</strong> (active/inactive + pairings + discount express ISG&apos;s
          manufacturer-network position).
        </p>
        <ul className="list-disc pl-5 text-xs space-y-0.5">
          {allEntries.map(([field, c]) => (
            <li key={field}>
              <code>{field}</code> — <span className="font-mono">{c.layer5Nature}</span>
              {c.rationale ? ` — ${c.rationale.split(';')[0]}` : ''}
            </li>
          ))}
        </ul>
      </LayerSection>

      <LayerSection layer={6} title="Operational vs ownership">
        <p>
          Per scope §6.1.4 + Sol D-amendment 2026-05-27 (sign-off commit 82e8bb0):
          ownership-altitude reads for <code>manufacturers.name</code> (identity-defining);
          operational-altitude for <code>is_active</code> + AMENDED{' '}
          <code>manufacturer_distributors.pairing_existence</code> (pairing toggles read as
          operating reality — ISG&apos;s network evolves day-to-day; parallel to
          distributor.is_active operational classification).
        </p>
        <ul className="list-disc pl-5 text-xs space-y-0.5">
          {allEntries
            .filter(([, c]) => c.layer6Authority !== undefined)
            .map(([field, c]) => (
              <li key={field}>
                <code>{field}</code> — <span className="font-mono">{c.layer6Authority}</span>
              </li>
            ))}
        </ul>
      </LayerSection>

      <LayerSection layer={7} title="Trace">
        <p>
          Per scope §6.1.4 L7: <code>manufacturers.provenance</code> sidecar exists (Day 67
          P2.2 7-table coverage); per-row trace at manufacturer altitude. Pairing trace
          (manufacturer_distributors) untraced — pairings table is a join table without
          provenance sidecar.
        </p>
      </LayerSection>

      <LayerSection layer={8} title="Propagation">
        <p>Curated propagation map (verified as of 2026-05-27 per scope §8 staleness marker):</p>
        <ul className="list-disc pl-5 text-xs space-y-2">
          {PER_MANUFACTURER_PROPAGATION.map((entry) => (
            <li key={entry.field}>
              <strong>
                <code>{entry.field}</code>
              </strong>{' '}
              edits affect:
              <ul className="list-circle pl-5 mt-1 space-y-0.5">
                {entry.affects.map((eff) => (
                  <li key={eff}>{eff}</li>
                ))}
              </ul>
            </li>
          ))}
        </ul>
      </LayerSection>

      <LayerSection layer={9} title="Reversibility">
        <p>
          Per scope §6.1.4 L9 + classification map: clean-reversible across manufacturer +
          pairing toggles (no past-decision residue at manufacturer altitude). Per-field:
        </p>
        <ul className="list-disc pl-5 text-xs space-y-0.5">
          {allEntries.map(([field, c]) => (
            <li key={field}>
              <code>{field}</code> — <span className="font-mono">{c.layer9Reversibility}</span>
            </li>
          ))}
        </ul>
      </LayerSection>

      <ComposabilityReservedLayer />

      <LayerSection layer={11} title="Positional contribution">
        <p>
          Substantive-contribution pattern per scope §6.1.4 L11.{' '}
          <code>manufacturer_distributors</code> pairings contribute to ISG&apos;s position by
          encoding which fulfillment paths are calculable per product — Path eligibility rule
          region (Atlas Day 3 §6.6.3) reads these pairings as substrate for path-availability
          gating.
        </p>
      </LayerSection>
    </InspectShell>
  );
}
